import React, {useEffect, useMemo, useState} from "react";
import AmendmentsPanel from "./components/amendments/amendments_panel";
import TabButton from "./components/amendments/tab_button";
import ItemContainer from "../helper_components/item_container";
import {processDocumentAmendments} from "utils/process_amendments";
import getIssueAmendments from "utils/amendments/get_issue_amendments";
import useSuppressedRenumberAmendments from "utils/amendments/use_suppressed_renumber_amendments";
import formatDate from "./utils/format_date";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import {isLlmIssueProcessing} from "common_components/checklist/util/llm_issue_is_processing";
import {
  AppliedAmendments,
  ClauseChangeError,
  DocumentAmendment,
  IssueResponse,
  ProcessedAmendment,
  ProcessedClauseChange,
  PreviewHeading,
} from "common/flowmaster/types/amendment";
import {Clausepart} from "common/types/document";
import type {
  OnLogAmendmentErrors,
  ScrollToAmendmentClause,
} from "./components/amendments/amendment_group";
import type {ApplyDocumentAmendmentsFn} from "./components/amendments/amendments_panel";

const styles = {
  tabBar: {
    display: "flex",
    borderBottom: "1px solid #e0e0e0",
    marginBottom: "12px",
    position: "sticky",
    top: 0,
    backgroundColor: "#fff",
    zIndex: 1,
  },
  accessOptionsButton: {
    marginBottom: "12px",
  },
  emptyChatMessage: {
    padding: "24px 16px",
    textAlign: "center",
    fontSize: "13px",
    color: "#888",
  },
  generatingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
    gap: "14px",
    padding: "48px 24px",
    minHeight: "180px",
  },
  generatingTitle: {
    fontSize: "15px",
    fontWeight: 600,
    color: "#333",
  },
  generatingSubtitle: {
    fontSize: "13px",
    color: "#888",
    maxWidth: "300px",
    lineHeight: 1.5,
  },
  noAmendmentsContainer: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "10px 14px",
    borderRadius: "6px",
    border: "1px solid #e0e0e0",
    backgroundColor: "#fafafa",
  },
  noAmendmentsIcon: {
    fontSize: "22px",
    color: "#9e9e9e",
    flexShrink: 0,
  },
  noAmendmentsText: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    minWidth: 0,
  },
  noAmendmentsTitle: {
    fontSize: "13px",
    fontWeight: 600,
    color: "#333",
    lineHeight: 1.3,
  },
  noAmendmentsSubtitle: {
    fontSize: "12px",
    color: "#888",
    lineHeight: 1.4,
  },
} as const;

export interface ChatAmendmentGroup {
  conversationItemId: number;
  conversationId: number;
  creationDate: string;
  createdBy: {
    id: number;
    username: string;
    first_name: string;
    last_name: string;
  };
  amendments: DocumentAmendment[];
  userPrompt?: string;
}

interface AmendmentsProps {
  issue: {
    id: number;
    llm_response?: {
      amendments?: {
        value?: {
          // TODO - simplify when dealing with a single amendments shape returned from LLM
          amendments?: DocumentAmendment[] | {amendments?: DocumentAmendment[]};
          report?: {
            value?: string;
          };
        };
      };
    };
    applied_amendments?: AppliedAmendments;
    issue_responses?: IssueResponse[];
    llm_meta?: {
      is_complete?: boolean;
    };
  };
  onAmendmentsOpen: () => void;
  amendmentsOpen: boolean;
  headerStyles?: React.CSSProperties;
  applyClauseChanges: (changes: ProcessedClauseChange[]) => void;
  clauseChangeErrors?: ClauseChangeError[];
  setClauseChangeError?: (clauseChangeId: string, message: string) => void;
  documentClauseparts: Clausepart[];
  documentHeadings?: PreviewHeading[];
  chatAmendments?: ChatAmendmentGroup[];
  onPopOut?: () => void;
  hideHeader?: boolean;
  onOpenConversation?: (conversationId: number) => void;
  activeConversationId?: number | null;
  applyDocumentAmendments?: ApplyDocumentAmendmentsFn;
  getIsTrackAllChangesOn?: () => Promise<boolean>;
  enableTrackAllChanges?: () => Promise<void>;
  myClausesContent?: React.ReactNode;
  onLogAmendmentErrors?: OnLogAmendmentErrors;
  bulkApplyingIds?: string[];
  onViewedConversationIdChange?: (id: number | null) => void;
  onOpenChat?: () => void;
  removeAppliedAmendments?: (amendmentIds: string[]) => void;
  scrollToClause?: ScrollToAmendmentClause;
  showDraftingTabs?: boolean;
}

function formatChatGroupLabel(
  group: ChatAmendmentGroup,
): {
  prompt?: string;
  meta: string;
} {
  const dateStr = formatDate(group.creationDate);
  const userName =
    group.createdBy?.first_name && group.createdBy?.last_name
      ? `${group.createdBy.first_name} ${group.createdBy.last_name}`
      : group.createdBy?.username ?? "Unknown";
  const meta = `${userName} · ${dateStr}`;
  if (group.userPrompt) {
    const prompt =
      group.userPrompt.length > 120
        ? `${group.userPrompt.slice(0, 120)}...`
        : group.userPrompt;
    return {prompt, meta};
  }
  return {meta};
}

const Amendments: React.FC<AmendmentsProps> = props => {
  const {
    issue,
    onAmendmentsOpen,
    amendmentsOpen,
    headerStyles,
    applyClauseChanges,
    clauseChangeErrors,
    setClauseChangeError,
    documentClauseparts,
    documentHeadings,
    chatAmendments = [],
    onPopOut,
    hideHeader,
    onOpenConversation,
    activeConversationId,
    applyDocumentAmendments,
    getIsTrackAllChangesOn,
    enableTrackAllChanges,
    myClausesContent,
    onLogAmendmentErrors,
    bulkApplyingIds,
    onViewedConversationIdChange,
    onOpenChat,
    removeAppliedAmendments,
    scrollToClause,
    showDraftingTabs,
  } = props;

  const rawAmendments = useMemo(() => getIssueAmendments(issue), [
    issue.llm_response?.amendments?.value?.amendments,
  ]);

  const allAmendments = useMemo(
    () => [
      ...rawAmendments,
      ...chatAmendments.flatMap(group => group.amendments),
    ],
    [rawAmendments, chatAmendments],
  );
  const {suppressed, amendmentsReady} = useSuppressedRenumberAmendments(
    allAmendments,
  );

  const deviationSummaries = useMemo(() => {
    const summaries: Record<string, string> = {};
    const reportValue = issue.llm_response?.amendments?.value?.report?.value;
    if (typeof reportValue === "string") {
      const jsonMatches = reportValue.matchAll(/```json\s*([\s\S]*?)\s*```/g);
      for (const match of jsonMatches) {
        try {
          const parsed = JSON.parse(match[1]);
          if (parsed.deviations) {
            for (const deviation of parsed.deviations) {
              const key = String(deviation.issue_response_id ?? "default");
              summaries[key] = deviation.issue_summary;
            }
          } else if (parsed.issue_summary) {
            summaries["default"] = parsed.issue_summary;
          }
        } catch {
          // Ignore parse errors for this block
        }
      }
    }
    return summaries;
  }, [issue.llm_response?.amendments?.value?.report?.value]);

  const amendments = useMemo(() => {
    const visibleRawAmendments = rawAmendments.filter(
      amendment => !suppressed.has(amendment),
    );
    if (visibleRawAmendments.length === 0) {
      return [];
    }
    const grouped: Record<string, DocumentAmendment[]> = {};
    for (const amendment of visibleRawAmendments) {
      const key = String(amendment.issue_response_id ?? "default");
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(amendment);
    }
    const result: ProcessedAmendment[] = [];
    const sortOrder: Record<string, number> = {"-1": 0, "-2": 1, default: 2};
    const sortedKeys = Object.keys(grouped).sort(
      (a, b) => (sortOrder[a] ?? 3) - (sortOrder[b] ?? 3),
    );
    for (const key of sortedKeys) {
      const group = grouped[key];
      const issueResponseName =
        key !== "default" &&
        issue.issue_responses?.find(ir => ir.id === Number(key))?.name;
      const label =
        key === "default"
          ? "Suggested surgical amendments"
          : key === "-1"
          ? "Amend using standard language"
          : key === "-2"
          ? "Amend using fallback language"
          : issueResponseName || `Drafting example #${key}`;
      const processed = processDocumentAmendments(group, label);
      const summary = deviationSummaries[key];
      if (summary) {
        for (const p of processed) {
          p.explanationMeta = summary;
        }
      }
      result.push(...processed);
    }
    return result;
  }, [rawAmendments, deviationSummaries, issue.issue_responses, suppressed]);

  const processedChatAmendments = useMemo((): ProcessedAmendment[] => {
    if (!chatAmendments || chatAmendments.length === 0) {
      return [];
    }
    const allProcessed: ProcessedAmendment[] = [];
    for (const group of chatAmendments) {
      const {prompt, meta} = formatChatGroupLabel(group);
      const visibleAmendments = group.amendments.filter(
        amendment => !suppressed.has(amendment),
      );
      const processed = processDocumentAmendments(
        visibleAmendments,
        prompt ?? meta,
      );
      for (const p of processed) {
        p.explanationMeta = meta;
        p.conversationId = group.conversationId;
        p.userPrompt = group.userPrompt;
      }
      allProcessed.push(...processed);
    }
    return allProcessed;
  }, [chatAmendments, suppressed]);

  const hasUploadAmendments = amendments.length > 0;
  const hasChatAmendments = processedChatAmendments.length > 0;
  const hasMyClausesContent = Boolean(myClausesContent);
  const showFullGeneratingState =
    isLlmIssueProcessing(issue) &&
    !hasUploadAmendments &&
    !hasChatAmendments &&
    !hasMyClausesContent;
  const showNoAmendmentsState =
    Boolean(issue.llm_meta?.is_complete) && !hasUploadAmendments;

  const defaultTab = hasUploadAmendments
    ? "initial"
    : hasChatAmendments
    ? "chat"
    : hasMyClausesContent
    ? "my_clauses"
    : null;

  const [activeTab, setActiveTab] = useState<
    "initial" | "chat" | "my_clauses" | null
  >(defaultTab);

  useEffect(() => {
    setActiveTab(prev => {
      const isCurrentTabValid =
        (prev === "initial" && hasUploadAmendments) ||
        prev === "chat" ||
        (prev === "my_clauses" && hasMyClausesContent);
      return isCurrentTabValid ? prev : defaultTab;
    });
  }, [defaultTab]);

  const sharedPanelProps = {
    appliedAmendments: issue.applied_amendments ?? {},
    applyClauseChanges,
    clauseChangeErrors: clauseChangeErrors ?? [],
    setClauseChangeError,
    documentClauseparts,
    documentHeadings,
    defaultExpanded: hideHeader,
    defaultShowChanges: hideHeader,
    applyDocumentAmendments,
    getIsTrackAllChangesOn,
    enableTrackAllChanges,
    onLogAmendmentErrors,
    bulkApplyingIds,
    removeAppliedAmendments,
    scrollToClause,
  };

  const accessOptionsButton = onPopOut ? (
    <Button
      variant="contained"
      color="primary"
      size="small"
      fullWidth
      startIcon={<OpenInNewIcon />}
      onClick={e => {
        e.stopPropagation();
        onPopOut();
      }}
      style={styles.accessOptionsButton}
    >
      Access all drafting options
    </Button>
  ) : undefined;

  const generatingState = (
    <div style={styles.generatingContainer}>
      <CircularProgress size={28} />
      <div style={styles.generatingTitle}>Generating amendments</div>
      <div style={styles.generatingSubtitle}>
        Suggested amendments for this issue are still being drafted and will
        appear here shortly.
      </div>
    </div>
  );

  const noAmendmentsState = (
    <div style={styles.noAmendmentsContainer}>
      <InfoOutlinedIcon style={styles.noAmendmentsIcon} />
      <div style={styles.noAmendmentsText}>
        <div style={styles.noAmendmentsTitle}>No amendments suggested</div>
        <div style={styles.noAmendmentsSubtitle}>
          Tiro did not suggest any amendments for this issue.
        </div>
      </div>
    </div>
  );

  const initialAmendmentsPanel =
    hasUploadAmendments && amendmentsReady ? (
      <AmendmentsPanel {...sharedPanelProps} amendments={amendments} stacked />
    ) : showNoAmendmentsState ? (
      noAmendmentsState
    ) : null;

  const tabbedContent = (
    <>
      <div style={styles.tabBar}>
        <TabButton
          label="Suggested Amendments"
          subLabel="Suggested surgical amendments"
          tabKey="initial"
          activeTab={activeTab}
          onClick={() => setActiveTab("initial")}
          disabled={!hasUploadAmendments}
          count={hasUploadAmendments ? amendments.length : undefined}
        />
        {hasMyClausesContent && (
          <TabButton
            label="My Clauses"
            subLabel="Request amendments based on example drafting"
            tabKey="my_clauses"
            activeTab={activeTab}
            onClick={() => setActiveTab("my_clauses")}
          />
        )}
        <TabButton
          label="Chat"
          subLabel="Request any amendments using chat"
          tabKey="chat"
          activeTab={activeTab}
          onClick={() => {
            setActiveTab("chat");
            onOpenChat?.();
          }}
          count={hasChatAmendments ? processedChatAmendments.length : undefined}
        />
        {onOpenChat && (
          <IconButton onClick={onOpenChat} title="Open chat" color="primary">
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        )}
      </div>
      {activeTab === "initial" && initialAmendmentsPanel}
      {activeTab === "chat" && hasChatAmendments && amendmentsReady && (
        <AmendmentsPanel
          {...sharedPanelProps}
          amendments={processedChatAmendments}
          onOpenConversation={onOpenConversation}
          activeConversationId={activeConversationId}
          onViewedConversationIdChange={onViewedConversationIdChange}
        />
      )}
      {activeTab === "chat" && !hasChatAmendments && (
        <div style={styles.emptyChatMessage}>
          Chat generated amendments will appear here
        </div>
      )}
      {activeTab === "my_clauses" && myClausesContent}
    </>
  );

  const content = showFullGeneratingState
    ? generatingState
    : showDraftingTabs
    ? tabbedContent
    : initialAmendmentsPanel;

  if (hideHeader) {
    return content;
  }

  return (
    <ItemContainer
      title="Amendments"
      expandable={true}
      onExpandMenu={onAmendmentsOpen}
      isMenuExpanded={amendmentsOpen}
      helpText="This section shows suggested amendments"
      headerStyles={headerStyles}
      containerStyles={{
        flex: "1 0 auto",
      }}
    >
      {accessOptionsButton}
      {content}
    </ItemContainer>
  );
};

export default Amendments;
